import { useCallback, useEffect, useState } from "react";
import { useApi } from "../api";
import "./dashboard.css";

/*
 * Business Dashboard – React port of the PHP dashboard.
 * All figures come from GET /api/dashboard (backend/dashboard.py), which runs the same SQL.
 * Display rules kept from PHP:
 *   - low-stock banner only when at least one product is below minimum_stock
 *   - service revenue banner only when there are service payments today
 *   - "Verify payments now" only for users whose role is admin
 *   - % change shown only when it isn't 0 (it is 0 when last month had no sales)
 */

const DefaultLink = ({ href, children, ...p }) => <a href={href} {...p}>{children}</a>;
const n0 = (v) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

function Delta({ value }) {
  if (!value) return null;
  const up = value > 0;
  return (
    <span className={`db-delta ${up ? "up" : "down"}`}>
      <i className={`fas ${up ? "fa-arrow-up" : "fa-arrow-down"}`} />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function Metric({ label, value, unit = "TZS", icon, tone, desc, delay }) {
  return (
    <div className="db-card db-metric db-in" style={{ animationDelay: `${delay}ms` }}>
      <span className={`db-ic ${tone}`}><i className={`fas ${icon}`} /></span>
      <div className="db-metric-label">{label}</div>
      <div className="db-metric-value mono">{n0(value)}{unit && <span className="unit">{unit}</span>}</div>
      <div className="db-metric-desc">{desc}</div>
    </div>
  );
}

function PerfRow({ icon, tone, label, value, unit, lastValue, change }) {
  return (
    <div className="db-perf">
      <div className="db-perf-left">
        <span className={`db-ic sm ${tone}`}><i className={`fas ${icon}`} /></span>
        <div>
          <div className="db-perf-label">{label}</div>
          <div className="db-perf-value mono">{n0(value)}{unit && <span className="unit">{unit}</span>}</div>
        </div>
      </div>
      <div className="db-perf-right">
        <Delta value={change} />
        <span className="db-last-label">Last month</span>
        <span className="db-last-val mono">{n0(lastValue)}</span>
      </div>
    </div>
  );
}

const classChip = (cls) => ({ service: "svc", store: "store" }[String(cls || "pos").toLowerCase()] || "pos");

export default function Dashboard({ userRole, LinkComponent }) {
  const api = useApi();
  const Link = LinkComponent || DefaultLink;
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setD(await api.get("/api/dashboard"));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setRefreshing(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const today = now.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });
  const monthLabel = now.toLocaleDateString("en-GB", { month: "short", year: "numeric" });

  return (
    <main className="db">
      <header className="db-head">
        <h1>Business <span>Dashboard</span></h1>
        <div className="db-head-right">
          <span className="db-date"><i className="fas fa-calendar" />{today}</span>
          <button type="button" className="db-refresh" onClick={load} disabled={refreshing} aria-label="Refresh figures" title="Refresh">
            <i className={`fas fa-rotate-right ${refreshing ? "fa-spin" : ""}`} />
          </button>
        </div>
      </header>

      {error && (
        <div className="db-alert" role="alert">
          <span className="db-alert-ic"><i className="fas fa-plug-circle-xmark" /></span>
          <p>Couldn't load the dashboard: {error}</p>
          <button type="button" className="db-btn-ghost" onClick={load}>Try again</button>
        </div>
      )}

      {!d && !error && <Skeleton />}

      {d && (
        <>
          {/* ── Low stock alert ── */}
          {d.low_stock_total > 0 && (
            <div className="db-alert db-in">
              <span className="db-alert-ic"><i className="fas fa-triangle-exclamation" /></span>
              <p>
                <strong>{plural(d.low_stock_total, "product")}</strong> below minimum stock levels.{" "}
                <Link href="availabilty">Review inventory</Link>
              </p>
              <span className="db-alert-count mono">{d.low_stock_total}</span>
            </div>
          )}

          {/* ── Today's service revenue ── */}
          {d.services.today_count > 0 && (
            <div className="db-svc db-in">
              <span className="db-ic teal"><i className="fas fa-briefcase" /></span>
              <div className="db-svc-body">
                <div className="db-svc-label">Today's service revenue</div>
                <div className="db-svc-val mono">{n0(d.services.today_total)} <span>TZS</span></div>
                <div className="db-svc-sub">{plural(d.services.today_count, "transaction")} recorded today</div>
              </div>
              <Link href="ServiceList" className="db-svc-link">View services <i className="fas fa-arrow-right" /></Link>
            </div>
          )}

          {/* ── Priority row ── */}
          <div className="db-priority">
            {/* Unverified payments */}
            <section className={`db-card db-pad db-in ${d.pending.count > 0 ? "attn" : ""}`} aria-labelledby="pend-h">
              <div className="db-card-head">
                <div className="db-card-head-left">
                  <span className="db-ic amber"><i className="fas fa-clock" /></span>
                  <div>
                    <h2 id="pend-h" className="db-card-title">Unverified payments</h2>
                    <div className="db-card-sub">POS, store and service payments waiting for verification</div>
                  </div>
                </div>
                {d.pending.count > 0 && (
                  <span className="db-badge amber"><i className="fas fa-circle-exclamation" />{d.pending.count} pending</span>
                )}
              </div>

              {d.pending.count > 0 ? (
                <>
                  <div className="db-pending-total">
                    <div className="db-pending-label">Total pending</div>
                    <div className="db-pending-amount mono">{n0(d.pending.total)}<span>TZS</span></div>
                    <div className="db-chips">
                      {d.pending.pos_store_total > 0 && (
                        <span className="db-chip pos"><i className="fas fa-cart-shopping" />POS/Store: {n0(d.pending.pos_store_total)} TZS</span>
                      )}
                      {d.pending.service_total > 0 && (
                        <span className="db-chip svc"><i className="fas fa-briefcase" />Services: {n0(d.pending.service_total)} TZS</span>
                      )}
                    </div>
                  </div>

                  <ul className="db-methods">
                    {d.pending.by_method.map((p) => (
                      <li key={`${p.payment_class}-${p.payment_method}`}>
                        <span className="db-method-left">
                          <i className="fas fa-credit-card" />
                          <span className="db-method-name">{p.payment_method}</span>
                          <span className={`db-class ${classChip(p.payment_class)}`}>{p.payment_class}</span>
                        </span>
                        <span className="mono">{n0(p.total_amount)} TZS</span>
                      </li>
                    ))}
                  </ul>

                  {userRole === "admin" && (
                    <Link href="verify-payment" className="db-verify">
                      <i className="fas fa-circle-check" /> Verify payments now
                    </Link>
                  )}
                </>
              ) : (
                <div className="db-clear">
                  <span className="db-clear-ic"><i className="fas fa-check" /></span>
                  <div className="db-clear-title">All verified</div>
                  <div className="db-clear-sub">No payments pending review</div>
                </div>
              )}
            </section>

            {/* Daily performance */}
            <section className="db-card db-pad db-in" style={{ animationDelay: "60ms" }} aria-labelledby="perf-h">
              <div className="db-card-head">
                <div className="db-card-head-left">
                  <span className="db-ic indigo"><i className="fas fa-chart-line" /></span>
                  <div>
                    <h2 id="perf-h" className="db-card-title">Daily performance</h2>
                    <div className="db-card-sub">Compared with the same days last month</div>
                  </div>
                </div>
                <span className="db-badge indigo">{monthLabel}</span>
              </div>
              <div className="db-perfs">
                <PerfRow icon="fa-calendar-check" tone="indigo" label="Days with sales"
                  value={d.performance.days_with_sales} lastValue={d.performance.last_days_with_sales} />
                <PerfRow icon="fa-arrow-trend-up" tone="green" label="Average sales per day" unit="TZS"
                  value={d.performance.avg_sales_per_day} lastValue={d.performance.last_avg_sales_per_day}
                  change={d.performance.sales_change} />
                <PerfRow icon="fa-sack-dollar" tone="blue" label="Average profit per day" unit="TZS"
                  value={d.performance.avg_profit_per_day} lastValue={d.performance.last_avg_profit_per_day}
                  change={d.performance.profit_change} />
              </div>
            </section>
          </div>

          {/* ── Cash & liquidity ── */}
          <h2 className="db-section">Cash &amp; liquidity</h2>
          <div className="db-metrics">
            <Metric delay={0} label="Cash on hand" value={d.balances.cash_balance} icon="fa-money-bill-wave" tone="green" desc="Physical cash balance" />
            <Metric delay={40} label="Bank balance" value={d.balances.bank_balance} icon="fa-building-columns" tone="blue" desc="Current bank account" />
            <Metric delay={80} label="Lipa Namba" value={d.balances.lipanumber} icon="fa-mobile-screen" tone="purple" desc="Mobile money balance" />
          </div>

          {/* ── Accounts overview ── */}
          <h2 className="db-section">Accounts overview</h2>
          <div className="db-metrics">
            <Metric delay={0} label="Accounts receivable" value={d.balances.ar_balance} icon="fa-file-invoice" tone="indigo" desc="Amount owed by customers" />
            <Metric delay={40} label="Inventory value" value={d.balances.inventory_balance} icon="fa-boxes-stacked" tone="amber" desc="Stock on hand value" />
            <Metric delay={80} label="Accounts payable" value={d.balances.accounts_payable_balance} icon="fa-file-invoice-dollar" tone="red" desc="Amount owed to suppliers" />
          </div>

          {/* ── Services & billing ── */}
          <h2 className="db-section">Services &amp; billing</h2>
          <div className="db-metrics">
            <Metric delay={0} label="This month's revenue" value={d.services.month_total} icon="fa-briefcase" tone="teal"
              desc={`${plural(d.services.month_count, "service transaction")}`} />
            <Metric delay={40} label="Active services" value={d.services.active} unit="" icon="fa-list-check" tone="indigo"
              desc={`${d.services.registered} total services registered`} />
            <Metric delay={80} label="Pending invoices" value={d.services.pending_total} icon="fa-file-lines" tone="amber"
              desc={`${plural(d.services.pending_count, "service payment")} awaiting verification`} />
          </div>
        </>
      )}
    </main>
  );
}

function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading dashboard">
      <div className="db-priority">
        <div className="db-card db-pad"><div className="db-skel" style={{ height: 220 }} /></div>
        <div className="db-card db-pad"><div className="db-skel" style={{ height: 220 }} /></div>
      </div>
      {[0, 1, 2].map((s) => (
        <div key={s} className="db-metrics" style={{ marginTop: "2.5rem" }}>
          {[0, 1, 2].map((c) => <div key={c} className="db-card db-metric"><div className="db-skel" style={{ height: 96 }} /></div>)}
        </div>
      ))}
    </div>
  );
}
